import React, { useState, useEffect } from 'react';
import {
  Search, Key, ArrowUpCircle, Lock, GitBranch, EyeOff,
  ChevronDown, ChevronUp, Play, Terminal,
} from 'lucide-react';
import OpsecBadge from './OpsecBadge';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Category icons + labels
const CATEGORIES = {
  recon: { icon: Search, label: 'Recon', color: 'text-blue-400' },
  credential: { icon: Key, label: 'Credentials', color: 'text-yellow-400' },
  privesc: { icon: ArrowUpCircle, label: 'PrivEsc', color: 'text-orange-400' },
  persistence: { icon: Lock, label: 'Persistence', color: 'text-purple-400' },
  lateral: { icon: GitBranch, label: 'Lateral', color: 'text-cyan-400' },
  evasion: { icon: EyeOff, label: 'Evasion', color: 'text-red-400' },
};

// Chains metadata
const CHAIN_INFO = {
  full_recon: { label: 'Full Recon', opsec: 3, icon: Search },
  cred_harvest: { label: 'Cred Harvest', opsec: 9, icon: Key },
  pivot_recon: { label: 'Pivot Scan', opsec: 5, icon: GitBranch },
  safe_migrate: { label: 'Safe Migrate', opsec: 6, icon: EyeOff },
};

/**
 * Categorised smart commands panel with OPSEC badges.
 *
 * Props:
 *   sessionType   – "meterpreter" | "shell"
 *   sessionActive – boolean
 *   onCommand     – (cmd: string) => void
 *   onChain       – (chainId: string) => void
 */
const SmartCommandPanel = ({ sessionType = 'meterpreter', sessionActive = false, onCommand, onChain }) => {
  const [commands, setCommands] = useState({});
  const [expanded, setExpanded] = useState({});

  // Fetch smart commands on mount / sessionType change
  useEffect(() => {
    const fetchCommands = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const res = await fetch(
          `${API_BASE}/api/v1/exploit/smart-commands?session_type=${sessionType}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.ok) {
          const data = await res.json();
          setCommands(data);
          // Default: expand recon
          setExpanded((prev) => ({ ...prev, recon: true }));
        }
      } catch (e) {
        // Fallback — use empty
        console.warn('Failed to fetch smart commands', e);
      }
    };
    fetchCommands();
  }, [sessionType]);

  const toggleCategory = (cat) => {
    setExpanded((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  return (
    <div className="space-y-3">
      {/* Categories */}
      {Object.entries(CATEGORIES).map(([catKey, catMeta]) => {
        const Icon = catMeta.icon;
        const catCommands = commands[catKey] || {};
        const isOpen = expanded[catKey];

        return (
          <div key={catKey} className="bg-brand-black rounded-lg border border-gray-700/50 overflow-hidden">
            {/* Category header */}
            <button
              onClick={() => toggleCategory(catKey)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-800/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Icon className={`w-4 h-4 ${catMeta.color}`} />
                <span className={`text-xs font-bold uppercase ${catMeta.color}`}>{catMeta.label}</span>
                <span className="text-xs text-gray-600">({Object.keys(catCommands).length})</span>
              </div>
              {isOpen ? (
                <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
              )}
            </button>

            {/* Commands */}
            {isOpen && (
              <div className="px-2 pb-2 space-y-1">
                {Object.entries(catCommands).map(([cmdKey, cmdInfo]) => {
                  const disabled = !sessionActive || !cmdInfo.available;
                  return (
                    <button
                      key={cmdKey}
                      onClick={() => !disabled && onCommand?.(cmdInfo.cmd)}
                      disabled={disabled}
                      className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-colors ${
                        disabled
                          ? 'opacity-40 cursor-not-allowed text-gray-500'
                          : 'text-gray-300 hover:bg-gray-700/50 hover:text-white cursor-pointer'
                      }`}
                      title={disabled && cmdInfo.requires ? `Requires ${cmdInfo.requires}` : cmdInfo.cmd}
                    >
                      <span className="font-mono truncate">{cmdKey}</span>
                      <OpsecBadge score={cmdInfo.opsec} showLabel />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Chains section */}
      <div className="bg-brand-black rounded-lg border border-brand-green/20 p-3">
        <div className="flex items-center gap-2 mb-2">
          <Play className="w-4 h-4 text-brand-green" />
          <span className="text-xs font-bold text-brand-green uppercase">Auto Chains</span>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {Object.entries(CHAIN_INFO).map(([chainId, info]) => {
            const ChainIcon = info.icon;
            return (
              <button
                key={chainId}
                onClick={() => sessionActive && onChain?.(chainId)}
                disabled={!sessionActive}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded border border-gray-700/50 text-xs text-gray-300 hover:border-brand-green/50 hover:text-brand-green transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChainIcon className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{info.label}</span>
                <OpsecBadge score={info.opsec} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SmartCommandPanel;
